import sha1 from 'js-sha1';
import {
  ANNOTATION_DATABASES,
  BASE_URI,
  HB_API_VERSION,
  NETWORK_VERSIONS,
  VERSION_IDENTIFIERS,
} from '../settings';
import history, { updateHistory } from '../history';
import { baseSlug, resolveSlug } from '../core/util';
import { filterIntegrationsByVersion } from '../core/networkUtils';
import {
  fetchJson,
  getFetchErrorMessage,
  isAbortError,
  readErrorDetail,
  SET_FETCH_ERROR,
  withRetryKey,
} from './FetchActions';

// Only newly-added failure cleanup is ownership-gated. Successful response
// ordering is owned the same way: only the newest request may apply a result.
const latestTermsFailureOwner = new Map();
const latestNetworkFailureOwner = new Map();

function getNetworkRequestKey(id) {
  return id && typeof id === 'object'
    ? `group:${id.group}:${id.idx}`
    : `root:${String(id)}`;
}

/*
 * Action types
 */
export const SELECT_NODE = 'SELECT_NODE';
export const SELECT_EDGE = 'SELECT_EDGE';
export const SET_EDGE_CUTOFF = 'SET_EDGE_CUTOFF';
export const SET_NODE_CUTOFF = 'SET_NODE_CUTOFF';

export const SELECT_INTEGRATION = 'SELECT_INTEGRATION';
export const RECEIVE_INTEGRATIONS = 'RECEIVE_INTEGRATIONS';

export const ADD_NETWORK = 'ADD_NETWORK';
export const REMOVE_NETWORK = 'REMOVE_NETWORK';

export const REQUEST_NETWORK = 'REQUEST_NETWORK';
export const RECEIVE_NETWORK = 'RECEIVE_NETWORK';

export const RECEIVE_EVIDENCE = 'RECEIVE_EVIDENCE';

export const REQUEST_TERMS = 'REQUEST_TERMS';
export const RECEIVE_TERMS = 'RECEIVE_TERMS';

export const RECEIVE_DATATYPES = 'RECEIVE_DATATYPES';
export const SELECT_DATATYPES = 'SELECT_DATATYPES';
export const ADD_DATATYPE = 'ADD_DATATYPE';
export const REMOVE_DATATYPE = 'REMOVE_DATATYPE';

export const RECEIVE_MULTINETWORK_EDGE = 'RECEIVE_MULTINETWORK_EDGE';

export const ADD_GENESET = 'ADD_GENESET';
export const REMOVE_GENESET = 'REMOVE_GENESET';

export const ADD_NETWORK_GROUP = 'ADD_NETWORK_GROUP';

export const SET_CURRENT_SEARCH_GENES = 'SET_CURRENT_SEARCH_GENES';
export const SET_CURRENT_SEARCH_TEXT = 'SET_CURRENT_SEARCH_TEXT';
export const SET_CURRENT_SEARCH_INTEGRATION = 'SET_CURRENT_SEARCH_INTEGRATION';
export const CLEAR_CURRENT_SEARCH = 'CLEAR_CURRENT_SEARCH';
export const SET_SEARCH_TARGET = 'SET_SEARCH_TARGET';
export const SELECT_GIANT_VERSION = 'SELECT_GIANT_VERSION';
export const SELECT_TISSUE = 'SELECT_TISSUE';
// Drives NetworkExtendedUniverseBanner. Set by fetchAnnotatedTerms
// based on (URL has extended_universe?) and (giantVersion === GIANT?).
// 'active' = URL flag present + GIANT page (cross-organism counts
// in effect); 'stripped' = URL flag present + non-GIANT page (flag
// silently dropped); 'inactive' = no URL flag (no banner, default).
export const SET_EXTENDED_UNIVERSE_STATUS = 'SET_EXTENDED_UNIVERSE_STATUS';

/*
 * Set the giant version. Thunk so we can also remap the
 * currently-selected tissue (if any) to its counterpart in the new
 * version — kidney <-> kidney-v3, global <-> global-v3 — instead of
 * dropping the selection. Mirrors fmdMachine's resolveSlug pattern.
 */
export function selectGiantVersion(version) {
  return (dispatch, getState) => {
    updateHistory({ giantVersion: version });
    dispatch({ type: SELECT_GIANT_VERSION, giantVersion: version });

    const state = getState();
    const currentTissue = state.currentSearch?.selectedTissue;
    if (!currentTissue) return;

    // Match FMD's fmdMachine.assignIntegrations: pre-filter
    // integrations to the new version so resolveSlug can only return
    // a version-correct slug (otherwise it falls back to the
    // wrong-version match).
    const allIntegrations = state.integrations || [];
    const filtered = filterIntegrationsByVersion(allIntegrations, version);
    const base = baseSlug(currentTissue.slug);
    const resolvedSlug = resolveSlug(base, version, filtered);

    if (resolvedSlug === currentTissue.slug) return;
    if (!resolvedSlug) {
      dispatch({ type: SELECT_TISSUE, tissue: null });
      return;
    }
    const resolvedTissue = filtered.find(item => item.slug === resolvedSlug);
    if (resolvedTissue) {
      dispatch({ type: SELECT_TISSUE, tissue: resolvedTissue });
    }
  };
}

/*
 * Action creator to track information about the current search query
 */
export function setCurrentSearchGenes(list) {
  return { type: SET_CURRENT_SEARCH_GENES, currentSearchGenes: list };
}

export function setCurrentSearchText(text) {
  return { type: SET_CURRENT_SEARCH_TEXT, currentSearchText: text };
}

export function setCurrentSearchIntegration(integration) {
  return {
    type: SET_CURRENT_SEARCH_INTEGRATION,
    currentSearchIntegration: integration,
  };
}

export function clearCurrentSearch() {
  return { type: CLEAR_CURRENT_SEARCH };
}

export function setSearchTarget(target) {
  return { type: SET_SEARCH_TARGET, searchTarget: target };
}

// Pre-submit tissue choice for the Network query path. Lives on the
// currentSearch slice so it survives the SearchBoxTable <-> PasteBoxTable
// swap when the user toggles bulk search.
export function selectTissue(tissue) {
  return { type: SELECT_TISSUE, tissue };
}

/*
 * Action creators for handling integrations for users to select and generate networks of
 */

export function receiveIntegrations(json) {
  updateHistory({ integrations: json });
  return { type: RECEIVE_INTEGRATIONS, integrations: json };
}

export function selectIntegration(integration, id) {
  updateHistory({ selectedIntegration: integration });
  return { type: SELECT_INTEGRATION, integration, id };
}

/*
 * Action creators for requesting and receiving terms for calculating enrichment
 */

export function requestTerms(id) {
  return { type: REQUEST_TERMS, id };
}

export function receiveTerms(json, id) {
  return { type: RECEIVE_TERMS, terms: json, id };
}

/*
 * Action creators for interacting with a network
 */

export function selectNode(selectedNode, id) {
  return { type: SELECT_NODE, selectedNode, id };
}

export function selectEdge(selectedEdge, id) {
  return { type: SELECT_EDGE, selectedEdge, id };
}

export function setNodeCutoff(nodeCutoff, id) {
  return { type: SET_NODE_CUTOFF, nodeCutoff, id };
}

export function setEdgeCutoff(edgeCutoff, id) {
  return { type: SET_EDGE_CUTOFF, edgeCutoff, id };
}

export function receiveEvidence(edge, json, id) {
  return { type: RECEIVE_EVIDENCE, edge, evidence: json, id };
}

/*
 *  Action creators for adding, removing user networks
 */

export function receiveNetwork(json, id) {
  return { type: RECEIVE_NETWORK, network: json, id };
}

export function requestNetwork(integration, id) {
  return { type: REQUEST_NETWORK, integration, id };
}

export function addNetwork(integration, genes, id) {
  return { type: ADD_NETWORK, integration, genes, id };
}

export function removeNetwork(id) {
  return { type: REMOVE_NETWORK, id };
}

/*
 *  Action creators for adding, removing dataset datatypes
 */
export function selectDatatypes(datatypes, id) {
  return { type: SELECT_DATATYPES, datatypes, id };
}

export function addDatatype(datatype, id) {
  return { type: ADD_DATATYPE, datatype, id };
}

export function removeDatatype(id, index) {
  return { type: REMOVE_DATATYPE, id, index };
}

export function receiveDatatypes(json) {
  return { type: RECEIVE_DATATYPES, datatypes: json };
}

export function receiveMultiNetworkEdge(edge, json, id) {
  return { type: RECEIVE_MULTINETWORK_EDGE, id, edge, edges: json };
}

/*
 * Action creators for adding and removing selected genesets, which will highlight
 * nodes in the networks
 */

export function addGeneset(geneset, id) {
  return { type: ADD_GENESET, geneset, id };
}

export function removeGeneset(id) {
  return { type: REMOVE_GENESET, id };
}

export function addNetworkGroup(group) {
  return { type: ADD_NETWORK_GROUP, id: { group } };
}

/*
 * Convenience actions that additionally update d3
 */

export function removeNetworkUpdateD3(id) {
  return (dispatch, getState) => {
    dispatch(removeNetwork(id));
    const { networkGroups } = getState();
    const { group } = id;
    const networks = networkGroups[group];

    const d3nets = networks.map(net => net.d3);
    d3nets.forEach(net => {
      net.bindNetworks(d3nets);
      net.updateStyling();
    });
  };
}

/*
 *  Action creators for API calls
 */
export function fetchIntegrations() {
  return async (dispatch, getState, { fetch }) => {
    const { integrations } = getState();
    if (integrations && integrations.length) return;

    const json = await fetchJson(
      fetch,
      dispatch,
      `${BASE_URI}integrations/?_=${HB_API_VERSION}`,
    );
    if (json === undefined) return;
    dispatch(receiveIntegrations(json));
  };
}

export function fetchEvidence(edge, id, integration, datatypes) {
  return async (dispatch, getState, { fetch }) => {
    const { networkGroups, giantVersion } = getState();

    // Evidence only available for GIANT (v1)
    if (giantVersion !== NETWORK_VERSIONS.GIANT) {
      return;
    }

    const { group, idx } = id;
    const network = networkGroups[group][idx];

    // Query for evidence using selected (or given) datatypes and integration
    if (!datatypes && network) datatypes = network.selectedDatatypes; // eslint-disable-line no-param-reassign
    if (!integration && network) integration = network.selectedIntegration; // eslint-disable-line no-param-reassign

    let url =
      `${BASE_URI}integrations/${integration.slug}/evidence/` +
      `?source=${edge.source.entrez}&target=${edge.target.entrez}` +
      `&giant_version=${giantVersion}`;
    if (datatypes) {
      datatypes.forEach(type => {
        url += `&datatypes=${type.slug}`;
      });
    }
    url += '&limit=50';

    const json = await fetchJson(fetch, dispatch, url);
    if (json === undefined) return;
    dispatch(receiveEvidence(edge, json, id));
  };
}

export function fetchAnnotatedTerms(genes, id) {
  return async (dispatch, getState, { fetch }) => {
    const requestKey = getNetworkRequestKey(id);
    const failureOwner = {};
    latestTermsFailureOwner.set(requestKey, failureOwner);
    // A superseded request's failure must not raise a banner over a newer result.
    const report = action => {
      if (
        action.type !== SET_FETCH_ERROR ||
        latestTermsFailureOwner.get(requestKey) === failureOwner
      )
        dispatch(action);
    };
    dispatch(requestTerms(id));
    let termsList = [];
    const promises = [];

    // Forward `?extended_universe=true` from the URL to the API.
    // The flag is power-user-only — no UI control sets it, the user
    // types it into the address bar to compare legacy cross-organism
    // counts against the human-only on the same gene+tissue page.
    // Anything other than the literal string "true" (including
    // "false", absent, etc.) is treated as the default human-only
    // mode so we don't surface a hidden third state.
    //
    // extended_universe is the legacy GIANT-only behavior; a
    // non-GIANT methodology (MAGE today, future ones tomorrow) was
    // loaded human-only and has no cross-organism counts to
    // surface, so we drop the URL flag silently rather than
    // forwarding a request the API would reject. We also rewrite
    // the address bar via history.replace to strip the
    // now-meaningless extended_universe param, so the URL doesn't
    // imply a mode that isn't being applied.
    const urlParams = new URLSearchParams(history?.location?.search || '');
    const extendedUniverseInUrl = urlParams.get('extended_universe') === 'true';
    const { giantVersion } = getState();
    const isGiant = giantVersion === NETWORK_VERSIONS.GIANT;
    let euStatus = 'inactive';
    if (extendedUniverseInUrl && isGiant) {
      euStatus = 'active';
    } else if (extendedUniverseInUrl && !isGiant) {
      euStatus = 'stripped';
      urlParams.delete('extended_universe');
      const remaining = urlParams.toString();
      const cleanedUrl = `${history.location.pathname}${
        remaining ? `?${remaining}` : ''
      }`;
      updateHistory({}, cleanedUrl);
    }
    dispatch({
      type: SET_EXTENDED_UNIVERSE_STATUS,
      status: euStatus,
    });
    const extendedUniverse = euStatus === 'active';
    const euSuffix = extendedUniverse ? '&extended_universe=true' : '';

    // Query each database for terms annotated with genes
    for (let k = 0, N = ANNOTATION_DATABASES.length; k < N; k += 1) {
      const db = ANNOTATION_DATABASES[k];
      const url = `${BASE_URI}terms/annotated/?entrez=${genes
        .map(gene => gene.entrez)
        .join('&entrez=')}&database=${db}${euSuffix}`;
      const promise = fetchJson(fetch, report, url);
      promises.push(promise);
    }

    // Wait for all of the promises to finish
    const values = await Promise.all(promises);
    const successfulValues = values.filter(terms => terms !== undefined);
    const hasFailures = successfulValues.length !== values.length;
    // A partial result may update state only while it owns failure cleanup.
    if (hasFailures && latestTermsFailureOwner.get(requestKey) !== failureOwner)
      return;
    successfulValues.forEach(terms => {
      termsList = termsList.concat(terms);
    });

    // Only the newest request may deliver terms, whichever way it settled.
    if (latestTermsFailureOwner.get(requestKey) !== failureOwner) return;
    if (!hasFailures || successfulValues.length > 0)
      dispatch(receiveTerms(termsList, id));
    if (latestTermsFailureOwner.get(requestKey) === failureOwner)
      latestTermsFailureOwner.delete(requestKey);
  };
}

// The newest datatypes request; older ones, even for the same version, must
// neither report nor apply anything.
let latestDatatypesRequest;

export function fetchDatatypes(version) {
  return async (dispatch, getState, { fetch }) => {
    const token = {};
    latestDatatypesRequest = token;
    const current = () =>
      latestDatatypesRequest === token && getState().giantVersion === version;
    // The version switch changes the URL, so the retry key is logical.
    const report = withRetryKey(action => {
      if (action.type !== SET_FETCH_ERROR || current()) dispatch(action);
    }, 'datatypes');
    const versionId = VERSION_IDENTIFIERS[version];
    const json = await fetchJson(
      fetch,
      report,
      `${BASE_URI}datatypes/?version=${versionId}`,
    );
    if (json === undefined) return undefined;
    // A slower response for a previous request owns no current state.
    if (!current()) return undefined;
    dispatch(receiveDatatypes(json));

    // Auto-select all datatypes for existing network groups
    const { networkGroups } = getState();
    if (networkGroups) {
      Object.keys(networkGroups).forEach(group => {
        networkGroups[group].forEach((_, idx) => {
          dispatch(selectDatatypes(json, { group, idx }));
        });
      });
    }
    return json;
  };
}

export function fetchMultiNetworkEdge(edge, id) {
  return async (dispatch, getState, { fetch }) => {
    const { networkGroups, giantVersion } = getState();

    // Edge details across all networks only available for GIANT (v1)
    if (giantVersion !== NETWORK_VERSIONS.GIANT) {
      return;
    }

    const { group, idx } = id;
    const network = networkGroups[group][idx];

    let url =
      `${BASE_URI}integrations/edges/` +
      `?source=${edge.source.entrez}&target=${edge.target.entrez}` +
      `&compendium_slug=human-compendium`;

    if (network) {
      network.selectedDatatypes.forEach(type => {
        url += `&datatypes=${type.slug}`;
      });
      if (network.selectedIntegration.context.term)
        url += `&database=${network.selectedIntegration.context.term.database.slug}`;
    }

    const json = await fetchJson(fetch, dispatch, url);
    if (json === undefined) return;
    dispatch(receiveMultiNetworkEdge(edge, json, id));
  };
}

export function fetchNetworkWithTerms(integration, genes, id, datatypes) {
  return async (dispatch, getState, { fetch }) => {
    const requestKey = getNetworkRequestKey(id);
    const failureOwner = {};
    latestNetworkFailureOwner.set(requestKey, failureOwner);
    dispatch(requestNetwork(integration, id));
    const state = getState();
    let url = `${BASE_URI}integrations/${
      integration.slug
    }/network/?giant_version=${state.giantVersion}&entrez=${genes
      .map(gene => gene.entrez)
      .join('&entrez=')}`;
    if (datatypes) {
      datatypes.forEach(type => {
        url += `&datatypes=${type.slug}`;
      });
    }
    const ownsFailure = () =>
      latestNetworkFailureOwner.get(requestKey) === failureOwner;
    // A transport or parse failure from a superseded request must not
    // overwrite the network a newer request has since delivered.
    const reportOwnedFailure = error => {
      if (!isAbortError(error) && ownsFailure())
        dispatch(receiveNetwork({ error: getFetchErrorMessage(error) }, id));
    };

    try {
      let response;
      try {
        response = await fetch(url);
      } catch (error) {
        reportOwnedFailure(error);
        return;
      }

      // If error, store message and bail
      if (!response.ok) {
        let error = getFetchErrorMessage(response);
        if (response.status === 404)
          error =
            'Unfortunately, the query gene(s) are not in our ' +
            'data compendium and no predictions are available.';
        // A 400 says why (for example the gene-count limit); show that
        if (response.status === 400)
          error = (await readErrorDetail(response)) || error;
        if (ownsFailure()) dispatch(receiveNetwork({ error }, id));
        return;
      }

      let json;
      try {
        json = await response.json();
      } catch (error) {
        reportOwnedFailure(error);
        return;
      }

      // An older success must not erase a newer request's error either.
      if (!ownsFailure()) return;
      if (json.mincut) dispatch(setEdgeCutoff(json.mincut, id));

      dispatch(receiveNetwork(json, id));

      // Initialize network with all data types
      if (!datatypes) {
        const { giantVersion } = getState();
        // Stored datatypes may belong to the previous network version.
        const loadedDatatypes = await dispatch(fetchDatatypes(giantVersion));
        // A newer request may have taken over while the datatypes loaded.
        if (!ownsFailure()) return;
        if (loadedDatatypes === undefined) return;
        dispatch(selectDatatypes(loadedDatatypes, id));
      }

      dispatch(fetchAnnotatedTerms(json.genes, id));
    } finally {
      if (ownsFailure()) latestNetworkFailureOwner.delete(requestKey);
    }
  };
}

export function fetchRelevantNetworks(genes, database) {
  const body = `{ "entrez": [${genes.map(gene => `"${gene.entrez}"`)}] }`;
  const bodyTag = sha1(body);
  let url = `${BASE_URI}integrations/relevant/?body_tag=${bodyTag}`;
  if (database) {
    url += `?database=${database}`;
  }
  return fetch(url, {
    method: 'POST',
    body,
    headers: { 'Content-Type': 'application/json' },
  });
}
